import { TypePolicies } from "@apollo/client";

/**
 * Since we use custom IDs, for each object added, we define the custom ID here for that type of object, to avoid
 * issues with the cache. See https://www.apollographql.com/docs/react/caching/cache-configuration/#customizing-cache-ids
 */
export const InMemoryCacheOptions: TypePolicies | undefined = {
  Query: {
    fields: {
      getUserReportList: {
        keyArgs: ['idUser', 'idTeam'],
        merge: (existing, incoming, options) => {
          // return base object if another does not exist
          if (!existing) {
            return incoming;
          }
          if (!incoming) {
            return existing;
          }
          // if we have nextToken, then we have to append and update the nextToken
          let existingUpdated = {...existing};
          if (!!options.args?.nextToken) {
            existingUpdated.reportList = [...existingUpdated.reportList, ...incoming.reportList];
            existingUpdated.nextToken = incoming.nextToken;
          } else {
            // if we don't have a nextToken in the call, this is a refetch for newest incoming transactions.
            // Let's put new values on top and remove duplicates
            existingUpdated.reportList = [...incoming.reportList, ...existingUpdated.reportList];
            // remove duplicates
            const seen = new Set();
            existingUpdated.reportList = existingUpdated.reportList.filter((i: { __ref: string }) => {
              const refValue = i.__ref;
              if (!seen.has(refValue)) {
                seen.add(refValue);
                return true;
              }
              return false;
            });
          }
          return existingUpdated;
        },
      },
      getTeamReportList: {
        keyArgs: ['idTeam'],
        merge: (existing, incoming, options) => {
          // return base object if another does not exist
          if (!existing) {
            return incoming;
          }
          if (!incoming) {
            return existing;
          }
          // if we have nextToken, then we have to append and update the nextToken
          let existingUpdated = {...existing};
          if (!!options.args?.nextToken) {
            existingUpdated.reportList = [...existingUpdated.reportList, ...incoming.reportList];
            existingUpdated.nextToken = incoming.nextToken;
          } else {
            // if we don't have a nextToken in the call, this is a refetch for newest incoming transactions.
            // Let's put new values on top and remove duplicates
            existingUpdated.reportList = [...incoming.reportList, ...existingUpdated.reportList];
            // remove duplicates
            const seen = new Set();
            existingUpdated.reportList = existingUpdated.reportList.filter((i: { __ref: string }) => {
              const refValue = i.__ref;
              if (!seen.has(refValue)) {
                seen.add(refValue);
                return true;
              }
              return false;
            });
          }
          return existingUpdated;
        },
      },
      getUserReportListByFilter: {
        keyArgs: false, // We'll handle the cache key manually
        merge: (existing, incoming, options) => {
          const filterInput = options.args?.getUserReportListByFilterInput;

          // Create a cache key from the filter values, excluding nextToken since it's used for pagination and
          // shouldn't affect the cache identity of the filter combination
          const cacheKey = JSON.stringify(
            Object.fromEntries(
              Object.entries(filterInput || {})
                .filter(([key]) => key !== 'nextToken')
            )
          );

          // If we have a new filter combination, ignore existing data
          if (options.storage?.cacheKey !== cacheKey) {
            options.storage.cacheKey = cacheKey;
            return incoming;
          }

          // return base object if another does not exist
          if (!existing) {
            return incoming;
          }
          if (!incoming) {
            return existing;
          }
          // if we have nextToken, then we have to append and update the nextToken
          let existingUpdated = {...existing};
          if (!!filterInput.nextToken) {
            existingUpdated.reportList = [...existingUpdated.reportList, ...incoming.reportList];
            existingUpdated.nextToken = incoming.nextToken;
          } else {
            // if we don't have a nextToken in the call, this is a refetch for newest incoming transactions.
            // Let's put new values on top and remove duplicates
            existingUpdated.reportList = [...existingUpdated.reportList, ...incoming.reportList];
            // remove duplicates
            const seen = new Set();
            existingUpdated.reportList = existingUpdated.reportList.filter((i: { __ref: string }) => {
              const refValue = i.__ref;
              if (!seen.has(refValue)) {
                seen.add(refValue);
                return true;
              }
              return false;
            });
          }
          return existingUpdated;
        },
      },
      getTeamReportListByFilter: {
        keyArgs: false, // We'll handle the cache key manually
        merge: (existing, incoming, options) => {
          const filterInput = options.args?.getTeamReportListByFilterInput;

          // Create a cache key from the filter values, excluding nextToken since it's used for pagination and
          // shouldn't affect the cache identity of the filter combination
          const cacheKey = JSON.stringify(
            Object.fromEntries(
              Object.entries(filterInput || {})
                .filter(([key]) => key !== 'nextToken')
            )
          );

          // If we have a new filter combination, ignore existing data
          if (options.storage?.cacheKey !== cacheKey) {
            options.storage.cacheKey = cacheKey;
            return incoming;
          }

          // return base object if another does not exist
          if (!existing) {
            return incoming;
          }
          if (!incoming) {
            return existing;
          }

          // if we have nextToken, then we have to append and update the nextToken
          let existingUpdated = {...existing};
          if (!!filterInput.nextToken) {
            existingUpdated.reportList = [...existingUpdated.reportList, ...incoming.reportList];
            existingUpdated.nextToken = incoming.nextToken;
          } else {
            // if we don't have a nextToken in the call, this is a refetch for newest incoming transactions.
            // Let's put new values on top and remove duplicates
            existingUpdated.reportList = [...incoming.reportList, ...existingUpdated.reportList];
            // remove duplicates
            const seen = new Set();
            existingUpdated.reportList = existingUpdated.reportList.filter((i: { __ref: string }) => {
              const refValue = i.__ref;
              if (!seen.has(refValue)) {
                seen.add(refValue);
                return true;
              }
              return false;
            });
          }
          return existingUpdated;
        },
      },
    },
  },
  Report: {
    keyFields: ['idReport'],
    fields: {
      // Replace nested report fields fully on each write.
      // Duplicated reports can reuse the same spot ids with different dates,
      // so we must not let Apollo merge nested report content heuristically.
      spotList: {
        merge: false,
      },
      targetList: {
        merge: false,
      },
      sharedWith: {
        merge: false,
      },
      filter: {
        merge: false,
      },
      scheduled: {
        merge: false,
      },
    },
  },
  Team: {
    keyFields: ["idTeam"]
  },
  User: {
    keyFields: ["idUser"]
  },
  UserPreview: {
    keyFields: ["idUser"]
  },
  // Spot ids are reused across different reports while dates can differ.
  // Keep spots nested under their parent report instead of normalizing by id.
  Spot: {
    keyFields: false,
  }
}
